import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const Container = styled.div`
  padding: 20px;
  background: ${props => props.theme?.surface || '#2d3748'};
  border-radius: 8px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const Title = styled.h2`
  color: ${props => props.theme?.text || '#e2e8f0'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PriceDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  padding: 15px 25px;
  border-radius: 12px;
  border: 1px solid #4a5568;
`;

const CurrentPrice = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #F7931A;
`;

const PriceLabel = styled.div`
  font-size: 12px;
  color: #a0aec0;
  text-transform: uppercase;
`;

const RefreshButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: #3182ce;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover { 
    background: #2b6cb0; 
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid ${props => props.active ? '#3182ce' : '#4a5568'};
  background: ${props => props.active ? '#3182ce' : 'transparent'};
  color: ${props => props.active ? 'white' : '#a0aec0'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 12px;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  &:hover {
    border-color: #3182ce;
    background: ${props => props.active ? '#3182ce' : 'rgba(49, 130, 206, 0.1)'};
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 25px;
`;

const SummaryCard = styled.div<{ priority?: string; clickable?: boolean }>`
  background: ${props => {
    switch(props.priority) {
      case 'BROKEN': return 'linear-gradient(135deg, #742a2a 0%, #9b2c2c 100%)';
      case 'MISS': return 'linear-gradient(135deg, #742a2a 0%, #9b2c2c 100%)';
      case 'SUCCESS': return 'linear-gradient(135deg, #22543d 0%, #2f855a 100%)';
      case 'EXPIRED': return 'linear-gradient(135deg, #744210 0%, #c05621 100%)';
      case 'CRITICAL': return 'linear-gradient(135deg, #744210 0%, #dd6b20 100%)';
      case 'HIGH': return 'linear-gradient(135deg, #744210 0%, #d69e2e 100%)';
      case 'MEDIUM': return 'linear-gradient(135deg, #285e61 0%, #38a169 100%)';
      default: return props.theme?.background || '#1a202c';
    }
  }};
  padding: 12px;
  border-radius: 10px;
  text-align: center;
  border: 1px solid ${props => props.priority ? 'transparent' : '#4a5568'};
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  
  &:hover {
    transform: ${props => props.clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => props.clickable ? '0 4px 12px rgba(0,0,0,0.3)' : 'none'};
  }
`;

const SummaryValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: white;
  margin-bottom: 3px;
`;

const SummaryLabel = styled.div`
  font-size: 10px;
  color: rgba(255,255,255,0.8);
  text-transform: uppercase;
  font-weight: 600;
`;

const PredictionTable = styled.div`
  background: ${props => props.theme?.background || '#1a202c'};
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #4a5568;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 35px 55px 80px 95px 1fr 85px 70px 70px 100px;
  padding: 12px 15px;
  background: #1a202c;
  border-bottom: 2px solid #4a5568;
  font-size: 10px;
  font-weight: 700;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 30px 45px 70px 80px 1fr 75px 60px 60px 90px;
    font-size: 9px;
  }
`;

const TableRow = styled.div<{ priority: string; selected?: boolean }>`
  display: grid;
  grid-template-columns: 35px 55px 80px 95px 1fr 85px 70px 70px 100px;
  padding: 12px 15px;
  border-bottom: 1px solid #374151;
  align-items: center;
  transition: background 0.2s;
  
  @media (max-width: 1200px) {
    grid-template-columns: 30px 45px 70px 80px 1fr 75px 60px 60px 90px;
    font-size: 12px;
  }
  
  ${props => props.selected && `
    outline: 2px solid #3182ce;
    outline-offset: -2px;
  `}
  
  background: ${props => {
    switch(props.priority) {
      case 'BROKEN': return 'rgba(229, 62, 62, 0.15)';
      case 'MISS': return 'rgba(229, 62, 62, 0.12)';
      case 'SUCCESS': return 'rgba(72, 187, 120, 0.12)';
      case 'EXPIRED': return 'rgba(237, 137, 54, 0.1)';
      case 'CRITICAL': return 'rgba(237, 137, 54, 0.08)';
      case 'HIGH': return 'rgba(236, 201, 75, 0.06)';
      default: return 'transparent';
    }
  }};
  
  &:hover {
    background: ${props => {
      switch(props.priority) {
        case 'BROKEN': return 'rgba(229, 62, 62, 0.25)';
        case 'MISS': return 'rgba(229, 62, 62, 0.2)';
        case 'SUCCESS': return 'rgba(72, 187, 120, 0.2)';
        case 'EXPIRED': return 'rgba(237, 137, 54, 0.2)';
        case 'CRITICAL': return 'rgba(237, 137, 54, 0.15)';
        case 'HIGH': return 'rgba(236, 201, 75, 0.12)';
        default: return '#374151';
      }
    }};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const PredictionId = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: #718096;
`;

const PriorityBadge = styled.span<{ priority: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  
  ${props => {
    switch(props.priority) {
      case 'BROKEN':
        return `
          background: #E53E3E;
          color: white;
          animation: ${pulse} 1.5s ease-in-out infinite;
        `;
      case 'MISS':
        return `
          background: #C53030;
          color: white;
        `;
      case 'SUCCESS':
        return `
          background: #38A169;
          color: white;
        `;
      case 'EXPIRED':
        return `
          background: #DD6B20;
          color: white;
        `;
      case 'CRITICAL':
        return `
          background: #ED8936;
          color: white;
        `;
      case 'HIGH':
        return `
          background: #ECC94B;
          color: #1a202c;
        `;
      case 'MEDIUM':
        return `
          background: #48BB78;
          color: white;
        `;
      case 'LOW':
        return `
          background: #718096;
          color: white;
        `;
      case 'PERMANENT':
        return `
          background: #805AD5;
          color: white;
        `;
      default:
        return `
          background: #4A5568;
          color: #A0AEC0;
        `;
    }
  }}
`;

const TypeBadge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  
  ${props => {
    switch(props.type) {
      case 'top':
        return `background: rgba(229, 62, 62, 0.2); color: #FC8181; border: 1px solid rgba(229, 62, 62, 0.3);`;
      case 'bottom':
        return `background: rgba(72, 187, 120, 0.2); color: #68D391; border: 1px solid rgba(72, 187, 120, 0.3);`;
      case 'target':
        return `background: rgba(66, 153, 225, 0.2); color: #63B3ED; border: 1px solid rgba(66, 153, 225, 0.3);`;
      case 'range':
      case 'pricetimerange':
        return `background: rgba(159, 122, 234, 0.2); color: #B794F4; border: 1px solid rgba(159, 122, 234, 0.3);`;
      case 'sentiment':
        return `background: rgba(237, 137, 54, 0.2); color: #F6AD55; border: 1px solid rgba(237, 137, 54, 0.3);`;
      case 'bet':
        return `background: rgba(236, 201, 75, 0.2); color: #F6E05E; border: 1px solid rgba(236, 201, 75, 0.3);`;
      default:
        return `background: rgba(160, 174, 192, 0.2); color: #A0AEC0; border: 1px solid rgba(160, 174, 192, 0.3);`;
    }
  }}
`;

const InfluencerName = styled.div`
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
`;

const PriceCell = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #F7931A;
  font-weight: 600;
`;

const TimeCell = styled.div<{ urgent?: boolean; expired?: boolean }>`
  font-family: monospace;
  font-size: 12px;
  font-weight: 600;
  color: ${props => {
    if (props.expired) return '#FC8181';
    if (props.urgent) return '#F6AD55';
    return '#a0aec0';
  }};
`;

const ProximityCell = styled.div`
  font-size: 11px;
  color: #a0aec0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #3182ce;
`;

const ActionButton = styled.button<{ variant?: 'success' | 'miss' | 'defer' }>`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  
  ${props => {
    switch(props.variant) {
      case 'success':
        return `background: #38A169; color: white; &:hover { background: #2F855A; }`;
      case 'miss':
        return `background: #E53E3E; color: white; &:hover { background: #C53030; }`;
      case 'defer':
        return `background: #718096; color: white; &:hover { background: #4A5568; }`;
      default:
        return `background: #4A5568; color: white; &:hover { background: #374151; }`;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 15px;
  background: linear-gradient(135deg, #2d3748 0%, #374151 100%);
  border-radius: 8px;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const BulkLabel = styled.span`
  font-size: 13px;
  color: #e2e8f0;
  font-weight: 600;
`;

const BulkButton = styled.button<{ variant?: 'success' | 'miss' | 'clear' }>`
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => {
    switch(props.variant) {
      case 'success':
        return `background: #38A169; color: white; &:hover { background: #2F855A; }`;
      case 'miss':
        return `background: #E53E3E; color: white; &:hover { background: #C53030; }`;
      case 'clear':
        return `background: transparent; color: #a0aec0; border: 1px solid #4a5568; &:hover { border-color: #718096; }`;
      default:
        return `background: #4A5568; color: white; &:hover { background: #374151; }`;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #a0aec0;
  font-size: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #4a5568;
  border-top-color: #3182ce;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 15px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #a0aec0;
`;

const TypeFilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  padding: 10px;
  background: #1a202c;
  border-radius: 8px;
`;

const TypeFilter = styled.button<{ active: boolean; type: string }>`
  padding: 5px 12px;
  border-radius: 15px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
  
  ${props => {
    const colors: Record<string, { bg: string; color: string; border: string }> = {
      all: { bg: props.active ? '#3182ce' : 'transparent', color: props.active ? 'white' : '#a0aec0', border: '#3182ce' },
      top: { bg: props.active ? 'rgba(229, 62, 62, 0.3)' : 'transparent', color: '#FC8181', border: 'rgba(229, 62, 62, 0.5)' },
      bottom: { bg: props.active ? 'rgba(72, 187, 120, 0.3)' : 'transparent', color: '#68D391', border: 'rgba(72, 187, 120, 0.5)' },
      target: { bg: props.active ? 'rgba(66, 153, 225, 0.3)' : 'transparent', color: '#63B3ED', border: 'rgba(66, 153, 225, 0.5)' },
      range: { bg: props.active ? 'rgba(159, 122, 234, 0.3)' : 'transparent', color: '#B794F4', border: 'rgba(159, 122, 234, 0.5)' },
      pricetimerange: { bg: props.active ? 'rgba(159, 122, 234, 0.3)' : 'transparent', color: '#B794F4', border: 'rgba(159, 122, 234, 0.5)' },
      sentiment: { bg: props.active ? 'rgba(237, 137, 54, 0.3)' : 'transparent', color: '#F6AD55', border: 'rgba(237, 137, 54, 0.5)' },
      bet: { bg: props.active ? 'rgba(236, 201, 75, 0.3)' : 'transparent', color: '#F6E05E', border: 'rgba(236, 201, 75, 0.5)' },
    };
    const c = colors[props.type] || colors.all;
    return `
      background: ${c.bg};
      color: ${c.color};
      border-color: ${props.active ? c.border : 'transparent'};
    `;
  }}
  
  &:hover {
    opacity: 0.8;
  }
`;

// Mobile Card View Components
const MobileCardList = styled.div`
  display: none;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileCard = styled.div<{ priority: string; selected?: boolean }>`
  background: ${props => props.theme?.background || '#1a202c'};
  border-radius: 12px;
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#3182ce' : '#4a5568'};
  transition: all 0.2s;
  
  ${props => {
    const bgColors: Record<string, string> = {
      'BROKEN': 'rgba(229, 62, 62, 0.1)',
      'MISS': 'rgba(229, 62, 62, 0.08)',
      'SUCCESS': 'rgba(72, 187, 120, 0.08)',
      'EXPIRED': 'rgba(237, 137, 54, 0.08)',
      'CRITICAL': 'rgba(237, 137, 54, 0.06)',
      'HIGH': 'rgba(236, 201, 75, 0.04)',
    };
    return `background: ${bgColors[props.priority] || '#1a202c'};`;
  }}
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 10px;
`;

const MobileCardTitle = styled.div`
  flex: 1;
`;

const MobileCardName = styled.div`
  font-weight: 600;
  color: #e2e8f0;
  font-size: 15px;
  margin-bottom: 4px;
`;

const MobileCardMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const MobileCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
`;

const MobileCardStat = styled.div`
  background: rgba(255,255,255,0.05);
  padding: 10px 12px;
  border-radius: 8px;
`;

const MobileStatLabel = styled.div`
  font-size: 10px;
  color: #718096;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 600;
`;

const MobileStatValue = styled.div<{ urgent?: boolean; expired?: boolean; color?: string }>`
  font-size: 16px;
  font-weight: 700;
  color: ${props => {
    if (props.color) return props.color;
    if (props.expired) return '#FC8181';
    if (props.urgent) return '#F6AD55';
    return '#e2e8f0';
  }};
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #374151;
`;

const MobileActionButton = styled.button<{ variant?: 'success' | 'miss' | 'hide' }>`
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  ${props => {
    switch(props.variant) {
      case 'success':
        return `background: #38A169; color: white; &:active { background: #2F855A; }`;
      case 'miss':
        return `background: #E53E3E; color: white; &:active { background: #C53030; }`;
      case 'hide':
        return `background: #718096; color: white; &:active { background: #4A5568; }`;
      default:
        return `background: #4A5568; color: white; &:active { background: #374151; }`;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MobileSelectRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const MobileCheckbox = styled.input`
  width: 22px;
  height: 22px;
  cursor: pointer;
  accent-color: #3182ce;
`;

// Tweet Preview Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #1a202c;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid #4a5568;
  
  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #4a5568;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #e2e8f0;
  font-size: 18px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #a0aec0;
  font-size: 24px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  
  &:hover {
    color: #e2e8f0;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ScreenshotPreview = styled.img`
  width: 100%;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #4a5568;
`;

const TweetText = styled.pre`
  background: #2d3748;
  padding: 16px;
  border-radius: 8px;
  color: #e2e8f0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0 0 16px 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  
  ${props => props.primary 
    ? `background: #3182ce; color: white; &:hover { background: #2b6cb0; }`
    : `background: #4a5568; color: #e2e8f0; &:hover { background: #718096; }`
  }
`;

const GenerateTweetButton = styled.button`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #805AD5;
  color: white;
  transition: all 0.2s;
  
  &:hover {
    background: #6B46C1;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MobileGenerateButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #805AD5;
  color: white;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:active {
    background: #6B46C1;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Types
interface Prediction {
  id: number;
  type: string;
  influencer: string;
  handle: string;
  targetPrice: string;
  currentPrice: number;
  daysRemaining: number | string;
  endDate: string;
  timePriority: string;
  pricePriority: string;
  overallPriority: string;
  priceProximity: number;
  priceDistance: number | null;
  priceDirection: string;
  isPermanent: boolean;
  isExpired: boolean;
  status: string;
  quote?: string;
}

interface MonitorData {
  success: boolean;
  currentPrice: number;
  priceDate: string;
  analyzedAt: string;
  summary: {
    total: number;
    needsAttention: number;
    expiringSoon: number;
    priceClose: number;
    byOverallPriority: Record<string, number>;
    byTimePriority: Record<string, number>;
    byPricePriority: Record<string, number>;
    byType: Record<string, number>;
  };
  predictions: Prediction[];
}

type ViewMode = 'all' | 'attention' | 'expiring' | 'price-close';

interface TweetPreview {
  id: number;
  text: string;
  screenshot?: string;
  evaluation?: {
    status: string;
    reason: string;
  };
}

const PriceMonitorPanel: React.FC = () => {
  const [data, setData] = useState<MonitorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('attention');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [tweetPreview, setTweetPreview] = useState<TweetPreview | null>(null);
  const [generatingTweet, setGeneratingTweet] = useState<number | null>(null);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await axios.get(`${apiBase}/api/admin/unified-monitor`);
      if (response.data.success) {
        setData(response.data);
        setLastRefresh(new Date());
        setError(null);
      } else {
        setError(response.data.error || 'Failed to load data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to server');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => fetchData(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatPrice = (price: number) => {
    return '$' + price.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const formatDays = (days: number | string) => {
    if (days === '∞' || days === Infinity) return '♾️';
    const d = Number(days);
    if (d <= 0) return `${Math.abs(d)}d ago`;
    if (d === 1) return '1 day';
    if (d < 7) return `${d} days`;
    if (d < 30) return `${Math.floor(d / 7)}w`;
    if (d < 365) return `${Math.floor(d / 30)}mo`;
    return `${(d / 365).toFixed(1)}y`;
  };

  const getTypeEmoji = (type: string) => {
    const emojis: Record<string, string> = {
      top: '📈',
      bottom: '📉',
      target: '🎯',
      range: '↔️',
      pricetimerange: '📊',
      sentiment: '💭',
      bet: '🎰',
      crash: '💥'
    };
    return emojis[type] || '•';
  };

  // Quick action to mark a prediction
  const markPrediction = async (id: number, status: string) => {
    setActionLoading(id);
    try {
      await axios.post(`${apiBase}/api/admin/predictions/${id}/status`, { status });
      // Refresh data after marking
      await fetchData(true);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk action
  const bulkMark = async (status: string) => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Mark ${selectedIds.size} predictions as ${status}?`)) return;
    
    setBulkLoading(true);
    try {
      await axios.post(`${apiBase}/api/admin/predictions/bulk-status`, {
        ids: Array.from(selectedIds),
        status
      });
      setSelectedIds(new Set());
      await fetchData(true);
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  // Toggle selection
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select all visible
  const selectAllVisible = () => {
    const visibleIds = filteredPredictions.map(p => p.id);
    setSelectedIds(new Set(visibleIds));
  };

  // Clear selection
  const clearSelection = () => setSelectedIds(new Set());

  // Generate tweet with screenshot
  const generateTweet = async (id: number) => {
    setGeneratingTweet(id);
    try {
      const response = await axios.post(`${apiBase}/api/admin/predictions/${id}/generate-tweet`);
      if (response.data.success) {
        setTweetPreview({
          id,
          text: response.data.draft.text,
          screenshot: response.data.screenshot?.url,
          evaluation: response.data.evaluation
        });
      } else {
        alert(`Error: ${response.data.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setGeneratingTweet(null);
    }
  };

  // Copy tweet to clipboard
  const copyTweet = () => {
    if (tweetPreview?.text) {
      navigator.clipboard.writeText(tweetPreview.text);
      alert('Tweet copied to clipboard!');
    }
  };

  const filteredPredictions = data?.predictions.filter(p => {
    // Type filter
    if (typeFilter !== 'all' && p.type !== typeFilter) return false;
    
    // View mode filter
    switch (viewMode) {
      case 'attention':
        return ['BROKEN', 'MISS', 'EXPIRED', 'CRITICAL', 'HIGH'].includes(p.overallPriority);
      case 'expiring':
        return ['CRITICAL', 'HIGH', 'EXPIRED'].includes(p.timePriority);
      case 'price-close':
        return ['BROKEN', 'SUCCESS', 'CRITICAL', 'HIGH'].includes(p.pricePriority);
      default:
        return true;
    }
  }) || [];

  if (loading) {
    return (
      <Container>
        <LoadingOverlay>
          <LoadingSpinner />
          Loading prediction monitor...
        </LoadingOverlay>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <EmptyState>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '10px' }}>Error Loading Monitor</div>
          <div>{error}</div>
          <RefreshButton onClick={() => fetchData()} style={{ marginTop: '20px' }}>
            🔄 Try Again
          </RefreshButton>
        </EmptyState>
      </Container>
    );
  }

  if (!data) return null;

  const summary = data.summary;

  return (
    <Container>
      {/* Tweet Preview Modal */}
      {tweetPreview && (
        <ModalOverlay onClick={() => setTweetPreview(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>📝 Tweet Draft #{tweetPreview.id}</ModalTitle>
              <CloseButton onClick={() => setTweetPreview(null)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {tweetPreview.screenshot && (
                <ScreenshotPreview 
                  src={`${apiBase}${tweetPreview.screenshot}`} 
                  alt="Prediction Screenshot" 
                />
              )}
              {tweetPreview.evaluation && (
                <div style={{ 
                  padding: '10px 14px', 
                  borderRadius: '8px', 
                  marginBottom: '16px',
                  background: tweetPreview.evaluation.status === 'success' 
                    ? 'rgba(72, 187, 120, 0.2)' 
                    : 'rgba(229, 62, 62, 0.2)'
                }}>
                  <strong>{tweetPreview.evaluation.status.toUpperCase()}</strong>: {tweetPreview.evaluation.reason}
                </div>
              )}
              <TweetText>{tweetPreview.text}</TweetText>
              <ModalActions>
                <ModalButton onClick={copyTweet}>
                  📋 Copy to Clipboard
                </ModalButton>
                <ModalButton primary onClick={() => {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetPreview.text)}`, '_blank');
                }}>
                  🐦 Open Twitter
                </ModalButton>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      <Header>
        <Title>
          📊 Unified Prediction Monitor
          {(summary.byOverallPriority.BROKEN || 0) > 0 && (
            <PriorityBadge priority="BROKEN">
              {summary.byOverallPriority.BROKEN} BROKEN
            </PriorityBadge>
          )}
          {(summary.byOverallPriority.EXPIRED || 0) > 0 && (
            <PriorityBadge priority="EXPIRED">
              {summary.byOverallPriority.EXPIRED} EXPIRED
            </PriorityBadge>
          )}
        </Title>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <PriceDisplay>
            <div>
              <PriceLabel>Current BTC</PriceLabel>
              <CurrentPrice>{formatPrice(data.currentPrice)}</CurrentPrice>
            </div>
          </PriceDisplay>
          
          <RefreshButton onClick={() => fetchData(true)} disabled={refreshing}>
            {refreshing ? '⏳' : '🔄'} {refreshing ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
        </div>
      </Header>

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard priority={summary.needsAttention > 0 ? 'CRITICAL' : undefined} clickable onClick={() => setViewMode('attention')}>
          <SummaryValue>{summary.needsAttention}</SummaryValue>
          <SummaryLabel>🚨 Needs Attention</SummaryLabel>
        </SummaryCard>
        <SummaryCard priority={summary.expiringSoon > 0 ? 'HIGH' : undefined} clickable onClick={() => setViewMode('expiring')}>
          <SummaryValue>{summary.expiringSoon}</SummaryValue>
          <SummaryLabel>⏰ Expiring Soon</SummaryLabel>
        </SummaryCard>
        <SummaryCard priority={summary.priceClose > 0 ? 'MEDIUM' : undefined} clickable onClick={() => setViewMode('price-close')}>
          <SummaryValue>{summary.priceClose}</SummaryValue>
          <SummaryLabel>💰 Price Close</SummaryLabel>
        </SummaryCard>
        <SummaryCard clickable onClick={() => setViewMode('all')}>
          <SummaryValue>{summary.total}</SummaryValue>
          <SummaryLabel>📋 Total Active</SummaryLabel>
        </SummaryCard>
        <SummaryCard priority={summary.byOverallPriority.SUCCESS ? 'SUCCESS' : undefined}>
          <SummaryValue>{summary.byOverallPriority.SUCCESS || 0}</SummaryValue>
          <SummaryLabel>✅ Success</SummaryLabel>
        </SummaryCard>
        <SummaryCard priority={summary.byOverallPriority.MISS ? 'MISS' : undefined}>
          <SummaryValue>{summary.byOverallPriority.MISS || 0}</SummaryValue>
          <SummaryLabel>❌ Miss</SummaryLabel>
        </SummaryCard>
      </SummaryGrid>

      {/* View Mode Tabs */}
      <TabBar>
        <TabButton active={viewMode === 'attention'} onClick={() => setViewMode('attention')}>
          🚨 Needs Attention ({summary.needsAttention})
        </TabButton>
        <TabButton active={viewMode === 'expiring'} onClick={() => setViewMode('expiring')}>
          ⏰ Expiring Soon ({summary.expiringSoon})
        </TabButton>
        <TabButton active={viewMode === 'price-close'} onClick={() => setViewMode('price-close')}>
          💰 Price Close ({summary.priceClose})
        </TabButton>
        <TabButton active={viewMode === 'all'} onClick={() => setViewMode('all')}>
          📋 All ({summary.total})
        </TabButton>
      </TabBar>

      {/* Type Filters */}
      <TypeFilterBar>
        <TypeFilter active={typeFilter === 'all'} type="all" onClick={() => setTypeFilter('all')}>
          All Types
        </TypeFilter>
        {Object.entries(summary.byType).map(([type, count]) => (
          <TypeFilter key={type} active={typeFilter === type} type={type} onClick={() => setTypeFilter(type)}>
            {getTypeEmoji(type)} {type} ({count})
          </TypeFilter>
        ))}
      </TypeFilterBar>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <BulkActionsBar>
          <BulkLabel>📦 {selectedIds.size} selected</BulkLabel>
          <BulkButton variant="success" onClick={() => bulkMark('success')} disabled={bulkLoading}>
            ✅ Mark Success
          </BulkButton>
          <BulkButton variant="miss" onClick={() => bulkMark('miss')} disabled={bulkLoading}>
            ❌ Mark Miss
          </BulkButton>
          <BulkButton onClick={() => bulkMark('hidden')} disabled={bulkLoading}>
            👁️‍🗨️ Hide
          </BulkButton>
          <BulkButton variant="clear" onClick={clearSelection}>
            Clear Selection
          </BulkButton>
        </BulkActionsBar>
      )}

      <PredictionTable>
        <TableHeader>
          <div>
            <Checkbox 
              type="checkbox" 
              checked={filteredPredictions.length > 0 && selectedIds.size === filteredPredictions.length}
              onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
            />
          </div>
          <div>ID</div>
          <div>Status</div>
          <div>Type</div>
          <div>Influencer</div>
          <div>Target</div>
          <div>Time ⏰</div>
          <div>Price 💰</div>
          <div>Actions</div>
        </TableHeader>
        
        {filteredPredictions.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>
              {viewMode === 'attention' ? '✨' : '📋'}
            </div>
            <div>
              {viewMode === 'attention' 
                ? 'No predictions need immediate attention!'
                : viewMode === 'expiring'
                  ? 'No predictions expiring soon'
                  : viewMode === 'price-close'
                    ? 'No predictions near price threshold'
                    : 'No predictions match the filter'}
            </div>
          </EmptyState>
        ) : (
          filteredPredictions.map(pred => (
            <TableRow key={pred.id} priority={pred.overallPriority} selected={selectedIds.has(pred.id)}>
              <div>
                <Checkbox 
                  type="checkbox"
                  checked={selectedIds.has(pred.id)}
                  onChange={() => toggleSelect(pred.id)}
                />
              </div>
              <PredictionId>#{pred.id}</PredictionId>
              <div>
                <PriorityBadge priority={pred.overallPriority}>
                  {pred.overallPriority}
                </PriorityBadge>
              </div>
              <div>
                <TypeBadge type={pred.type}>
                  {getTypeEmoji(pred.type)} {pred.type}
                </TypeBadge>
              </div>
              <InfluencerName title={`${pred.influencer} (@${pred.handle})`}>
                {pred.influencer}
              </InfluencerName>
              <PriceCell title={`Target: ${pred.targetPrice}`}>
                {pred.targetPrice.length > 12 
                  ? pred.targetPrice.substring(0, 10) + '...'
                  : pred.targetPrice}
              </PriceCell>
              <TimeCell 
                expired={pred.isExpired} 
                urgent={['CRITICAL', 'HIGH'].includes(pred.timePriority)}
                title={pred.endDate}
              >
                {pred.isPermanent ? '♾️' : formatDays(pred.daysRemaining)}
              </TimeCell>
              <div>
                <PriorityBadge priority={pred.pricePriority}>
                  {pred.pricePriority}
                </PriorityBadge>
              </div>
              <ActionsCell>
                <ActionButton 
                  variant="success" 
                  onClick={() => markPrediction(pred.id, 'success')}
                  disabled={actionLoading === pred.id}
                  title="Mark as Success"
                >
                  ✓
                </ActionButton>
                <ActionButton 
                  variant="miss" 
                  onClick={() => markPrediction(pred.id, 'miss')}
                  disabled={actionLoading === pred.id}
                  title="Mark as Miss"
                >
                  ✗
                </ActionButton>
                <ActionButton 
                  variant="defer" 
                  onClick={() => markPrediction(pred.id, 'hidden')}
                  disabled={actionLoading === pred.id}
                  title="Hide from monitoring"
                >
                  👁️
                </ActionButton>
                <GenerateTweetButton 
                  onClick={() => generateTweet(pred.id)}
                  disabled={generatingTweet === pred.id}
                  title="Generate tweet with screenshot"
                >
                  {generatingTweet === pred.id ? '⏳' : '📝'}
                </GenerateTweetButton>
              </ActionsCell>
            </TableRow>
          ))
        )}
      </PredictionTable>

      {/* Mobile Card View */}
      <MobileCardList>
        {filteredPredictions.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>
              {viewMode === 'attention' ? '✨' : '📋'}
            </div>
            <div>
              {viewMode === 'attention' 
                ? 'No predictions need immediate attention!'
                : viewMode === 'expiring'
                  ? 'No predictions expiring soon'
                  : viewMode === 'price-close'
                    ? 'No predictions near price threshold'
                    : 'No predictions match the filter'}
            </div>
          </EmptyState>
        ) : (
          filteredPredictions.map(pred => (
            <MobileCard key={pred.id} priority={pred.overallPriority} selected={selectedIds.has(pred.id)}>
              <MobileSelectRow>
                <MobileCheckbox 
                  type="checkbox"
                  checked={selectedIds.has(pred.id)}
                  onChange={() => toggleSelect(pred.id)}
                />
                <PriorityBadge priority={pred.overallPriority}>
                  {pred.overallPriority}
                </PriorityBadge>
              </MobileSelectRow>
              
              <MobileCardHeader>
                <MobileCardTitle>
                  <MobileCardName>{pred.influencer}</MobileCardName>
                  <MobileCardMeta>
                    <TypeBadge type={pred.type}>
                      {getTypeEmoji(pred.type)} {pred.type}
                    </TypeBadge>
                    <PredictionId>#{pred.id}</PredictionId>
                  </MobileCardMeta>
                </MobileCardTitle>
              </MobileCardHeader>

              <MobileCardBody>
                <MobileCardStat>
                  <MobileStatLabel>Target</MobileStatLabel>
                  <MobileStatValue color="#F7931A">
                    {pred.targetPrice.length > 15 
                      ? pred.targetPrice.substring(0, 12) + '...'
                      : pred.targetPrice}
                  </MobileStatValue>
                </MobileCardStat>
                <MobileCardStat>
                  <MobileStatLabel>Time Left</MobileStatLabel>
                  <MobileStatValue 
                    expired={pred.isExpired} 
                    urgent={['CRITICAL', 'HIGH'].includes(pred.timePriority)}
                  >
                    {pred.isPermanent ? '♾️ Forever' : formatDays(pred.daysRemaining)}
                  </MobileStatValue>
                </MobileCardStat>
                <MobileCardStat>
                  <MobileStatLabel>Time Status</MobileStatLabel>
                  <PriorityBadge priority={pred.timePriority}>
                    {pred.timePriority}
                  </PriorityBadge>
                </MobileCardStat>
                <MobileCardStat>
                  <MobileStatLabel>Price Status</MobileStatLabel>
                  <PriorityBadge priority={pred.pricePriority}>
                    {pred.pricePriority}
                  </PriorityBadge>
                </MobileCardStat>
              </MobileCardBody>

              <MobileCardActions>
                <MobileActionButton 
                  variant="success" 
                  onClick={() => markPrediction(pred.id, 'success')}
                  disabled={actionLoading === pred.id}
                >
                  ✓ Success
                </MobileActionButton>
                <MobileActionButton 
                  variant="miss" 
                  onClick={() => markPrediction(pred.id, 'miss')}
                  disabled={actionLoading === pred.id}
                >
                  ✗ Miss
                </MobileActionButton>
                <MobileActionButton 
                  variant="hide" 
                  onClick={() => markPrediction(pred.id, 'hidden')}
                  disabled={actionLoading === pred.id}
                >
                  👁️ Hide
                </MobileActionButton>
              </MobileCardActions>
              <MobileGenerateButton
                onClick={() => generateTweet(pred.id)}
                disabled={generatingTweet === pred.id}
              >
                {generatingTweet === pred.id ? '⏳ Generating...' : '📝 Generate Tweet'}
              </MobileGenerateButton>
            </MobileCard>
          ))
        )}
      </MobileCardList>
      
      {lastRefresh && (
        <div style={{ marginTop: '15px', fontSize: '12px', color: '#718096', textAlign: 'right' }}>
          Last updated: {lastRefresh.toLocaleTimeString()} • {filteredPredictions.length} predictions shown
        </div>
      )}
    </Container>
  );
};

export default PriceMonitorPanel;
